import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { MarginProperties } from '@iheartjane/reefer';
import { Box, Flex, Skeleton, Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { useCallback, useMemo } from 'react';
import { useLocation, useMatches, useNavigate, useParams } from 'react-router';

import type { UseFetchPlacement } from '@jane/dm/internal';
import { useRecommendedSort } from '@jane/dm/internal';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { searchOptions } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useRowContext,
  useShouldKioskShowGold,
  useThemeGenerator,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  KioskThemeProvider,
  useBrandSpecials,
  useGoldFacetsAndFilters,
  useMenu,
  useProductSort,
  useSearchContext,
} from '@jane/kiosk/providers';
import type { OptionalMenuRouteParams } from '@jane/kiosk/routing/types';
import {
  getSource,
  isSupportedStoreSpecial,
  trackSortedProductList,
} from '@jane/kiosk/util';
import type { ActiveFilters, FilterSortOption } from '@jane/search/data-access';
import { makeCurrentSort } from '@jane/search/data-access';
import {
  formatActiveFilters,
  handleFilterChange,
  overrideCustomCategoryLabels,
  updateFacetCounts,
} from '@jane/search/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

import { FilterChips } from '../FilterChips/FilterChips';
import { FiltersButton } from '../FiltersButton/FiltersButton';
import { JaneGoldCallout } from '../JaneGoldCallout/JaneGoldCallout';
import { getColumnCount } from '../ProductCard/ProductCard';
import { SortButton } from '../SortButton';
import { JaneGoldFilter } from './JaneGoldFilter';
import { SpecialQuickFilter } from './SpecialQuickFilter';
import { SubcategoryQuickFilter } from './SubcategoryQuickFilter';

export interface FilterAndSortProps extends MarginProperties {
  condensedView?: boolean;
  defaultSortName: Nullable<string>;
  hideFilterBar?: boolean;
  optionalFilters: Store['optional_filters'];
  searchProps: UseFetchPlacement;
  showAlphabeticalSort?: boolean;
  showTHCSort?: boolean;
}

export const FilterAndSort = ({
  condensedView = false,
  defaultSortName,
  hideFilterBar = false,
  optionalFilters,
  showAlphabeticalSort,
  showTHCSort,
  searchProps,
}: FilterAndSortProps) => {
  const showGold = useShouldKioskShowGold();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { categoryFacets } = useMenu();
  const { category = 'all', specialId } = useParams<OptionalMenuRouteParams>();
  const { pathname } = useLocation();
  const matches = useMatches();
  const navigate = useNavigate();
  const { getKioskSpacing } = useKioskSpacing();
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const blackTheme = useThemeGenerator('#000000');
  const { resetSearchState, searchState, setSearchState } = useSearchContext();
  const { currentSort, setCurrentSort, sortOptions, searchSort } =
    useProductSort({
      defaultSortName,
      hasThcProducts: showTHCSort || Boolean(categoryFacets?.['percent_thc']),
      optionalFilters,
      showAlphabeticalSort,
      store,
    });

  const { featuredCategory, featuredFilter, rowId } = useRowContext(store);
  const { brandSpecials, brandSpecialFilter } = useBrandSpecials();

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special)
  );

  const storeData = {
    customLabels: {
      category: store?.custom_lineage_labels,
      root_types: overrideCustomCategoryLabels(
        store?.state === 'California',
        store?.custom_product_type_labels
      ),
    },
    specials: supportedStoreSpecials,
    storeId: store?.id,
  };
  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const activeFilters = formatActiveFilters<
    Store,
    StoreSpecial | V3StoreSpecial
  >(searchState, storeData);

  const isSpendXGetY = specialInScope?.special_type === 'spending_threshold';
  const isGold = category === 'jane-gold';
  const staticSearchOptions = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
    storeSpecial: specialInScope,
  });

  const {
    searchResultFacets,
    numHits: filteredProductCount,
    isLoading,
  } = searchProps;

  /**Gold facets need to be updated separately due to user segmentation making the
   * count more complicated to calculate.
   */
  const { goldFacets } = useGoldFacetsAndFilters({
    goldFacetsEnabled:
      showGold && Boolean(categoryFacets?.['has_brand_discount']?.['true']),
    staticFilters: staticSearchOptions.staticFilters,
  });

  const presentedFacets = searchResultFacets
    ? updateFacetCounts(
        searchState,
        { ...cloneDeep(categoryFacets), ...goldFacets },
        searchResultFacets
      )
    : categoryFacets;

  const disableJaneGold = !presentedFacets?.['has_brand_discount']?.['true'];

  /**
   * Though similar to the one that includes more of searchState, this search is used to inform Mixpanel of the total number of results
   * _before_ the customer begins to filter. This is a general search that applies to multiple menu screens -
   * All products, Category, Featured, Special, Search Results.
   * @todo Can we get rid of this call? it seems to only want to get the initial product count and not the filtered count.
   * Is there a way we can store this somewhere and not have to make this call?
   */
  const { numHits: totalProductCount } = useRecommendedSort({
    maxProducts: 0,
    numColumns: getColumnCount(),
    searchAttributes: ['*'],
    searchFilter: staticSearchOptions.staticFilters,
    searchQuery: searchState.searchText ?? '',
    searchSort,
    storeId,
  });

  const onClearAll = useCallback(() => {
    resetSearchState({
      searchText: searchState.searchText,
    });
  }, [resetSearchState, searchState]);

  const onClearFilter = useCallback(
    ({ key, value }: ActiveFilters) => {
      const newSearchState = handleFilterChange(key, value, searchState);
      setSearchState(newSearchState);
    },
    [searchState, setSearchState]
  );

  const onFilter = useCallback(() => {
    navigate(`${pathname}/filters`);
  }, [navigate, pathname]);

  const onSortItemPress = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (newSort: FilterSortOption) => {
      trackSortedProductList({
        dealName: specialInScope ? [specialInScope.title] : null,
        defaultSort: [sortOptions[0].label],
        selectedSort: [newSort.label],
        source: getSource({
          currentSpecial: specialInScope,
          isTopper: false,
          matches,
          pathname,
        }),
        totalProductCount,
      });
      setCurrentSort(makeCurrentSort(newSort));
    },
    [
      matches,
      pathname,
      setCurrentSort,
      sortOptions,
      // eslint-disable-next-line react-hooks/preserve-manual-memoization
      specialInScope,
      totalProductCount,
    ]
  );

  const showGoldCallout =
    showGold &&
    store?.state === 'Ohio' &&
    activeFilters.some(
      (filter) => filter.key === 'has_brand_discount' && filter.value === 'true'
    );

  /** If there are no products to show without any filters applied, and the filter bar is set to be hidden, do not render the component */
  if (totalProductCount === 0 && hideFilterBar) {
    return null;
  }

  return (
    <KioskThemeProvider
      theme={
        /** Present filters in a black theme, regardless of store's theme. */
        blackTheme
      }
      themeId="filter-and-sort"
    >
      <Flex
        position="relative"
        zIndex="var(--layers-visible)"
        flexDirection="column"
        mb={16}
        mt={32}
        px={getKioskSpacing(64, 48)}
        gap={getKioskSpacing(32, 24)}
        width="100%"
      >
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          id="filter-and-sort"
          alignItems="center"
        >
          <Flex flexDirection="row" gap={16}>
            {condensedView ? (
              <>
                {isLoading && (
                  <Skeleton animate>
                    <Skeleton.Bone width="180px" />
                  </Skeleton>
                )}
                {!isLoading && filteredProductCount !== 0 && (
                  <Typography as="h4" variant="h3-semibold">
                    {pluralize({
                      noun: pathname.includes('search')
                        ? 'Product result'
                        : 'Product',
                      number: filteredProductCount,
                    })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <FiltersButton
                  hasActiveFilters={activeFilters.length > 0}
                  onClick={onFilter}
                />
                {/* TODO remove spx check when kiosk gold facets are moved off of algolia */}
                {showGold && brandSpecials?.length > 0 && !isSpendXGetY && (
                  <JaneGoldFilter disabled={disableJaneGold} />
                )}
                <SubcategoryQuickFilter
                  category={category}
                  filteredProductCount={filteredProductCount}
                  presentedFacets={presentedFacets}
                  totalProductCount={totalProductCount}
                />
                {!featuredCategory && !rowId && !specialId && (
                  <SpecialQuickFilter
                    filteredProductCount={filteredProductCount}
                    presentedFacets={presentedFacets}
                    supportedStoreSpecials={supportedStoreSpecials}
                    totalProductCount={totalProductCount}
                  />
                )}
              </>
            )}{' '}
          </Flex>
          {condensedView && (
            <Box ml="auto">
              <FiltersButton
                hasActiveFilters={activeFilters.length > 0}
                onClick={onFilter}
              />
            </Box>
          )}
          <SortButton
            aria-label="sort menu products"
            currentSort={currentSort}
            onSort={onSortItemPress}
            options={sortOptions}
          />
        </Flex>

        <FilterChips
          activeFilters={activeFilters}
          onClearAll={onClearAll}
          onClearFilter={onClearFilter}
        />

        {showGoldCallout && (
          <JaneGoldCallout variant="user" storeState={store.state} />
        )}
      </Flex>
    </KioskThemeProvider>
  );
};
